import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserCheck, UserX, CalendarCheck, ChevronLeft, ChevronRight, Calendar,
  Search, Phone, MessageCircle, ArrowRight, Clock, DollarSign, Stethoscope,
  RefreshCw, CheckCircle2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, telLink, waLink, todayIST, fmtDate, fmtDateTime, initials } from "@/lib/format";
import { PageHeader, Loading, EmptyState, StatCard } from "@/components/common";
import TreatmentRecordDialog from "@/components/TreatmentRecordDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function PresentPatients() {
  const navigate = useNavigate();
  const today = todayIST();
  const [selectedDate, setSelectedDate] = useState(today);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ present: 0, absent: 0, expected: 0, manual_absent: 0, auto_absent: 0 });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [treatmentOpen, setTreatmentOpen] = useState(false);
  const [selectedForTreatment, setSelectedForTreatment] = useState(null);

  // Fetch attendance list and summary for selected date
  const loadData = useCallback(async (dateToFetch) => {
    setLoading(true);
    try {
      const [attRes, sumRes] = await Promise.all([
        api.get("/attendance/today", { params: { date: dateToFetch } }),
        api.get("/attendance/summary", { params: { date: dateToFetch } }),
      ]);
      setItems(attRes.data.items || []);
      setSummary(sumRes.data || { present: 0, absent: 0, expected: 0 });
    } catch (e) {
      toast.error(apiError(e, "Failed to load present patients"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(selectedDate);
  }, [selectedDate, loadData]);

  // Date Navigation Helpers
  const shiftDate = (days) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    const yyyy = current.getFullYear();
    const mm = String(current.getMonth() + 1).padStart(2, "0");
    const dd = String(current.getDate()).padStart(2, "0");
    setSelectedDate(`${yyyy}-${mm}-${dd}`);
  };

  const setToday = () => {
    setSelectedDate(today);
  };

  // Filter only "present" status and apply search query
  const presentPatients = useMemo(() => {
    const presents = items.filter((item) => item.status === "present");
    const q = search.toLowerCase().trim();
    if (!q) return presents;
    return presents.filter(
      (p) =>
        (p.patient_name || "").toLowerCase().includes(q) ||
        (p.phone || "").includes(q) ||
        (p.patient_code || "").toLowerCase().includes(q) ||
        (p.problem || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const totalAbsent = (summary.absent ?? (summary.manual_absent || 0) + (summary.auto_absent || 0));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Present Patients"
        subtitle={`Showing attendance records for ${fmtDate(selectedDate)}`}
        testid="present-patients-page"
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadData(selectedDate)}
          className="gap-1.5"
          title="Refresh attendance"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </PageHeader>

      {/* Date Filter & Selector Bar */}
      <Card className="p-4 bg-slate-50/70 border-slate-200">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 bg-white"
              onClick={() => shiftDate(-1)}
              title="Previous Day"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="relative flex items-center">
              <Calendar className="absolute left-3 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
                className="pl-9 pr-3 h-9 bg-white font-medium text-slate-700 w-44"
              />
            </div>

            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 bg-white"
              onClick={() => shiftDate(1)}
              title="Next Day"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>

            {selectedDate !== today && (
              <Button
                variant="secondary"
                size="sm"
                className="h-9 text-xs font-semibold bg-white border text-bk-blue hover:bg-slate-100"
                onClick={setToday}
              >
                Go to Today
              </Button>
            )}
          </div>

          {/* Search Box */}
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search name, phone, code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 bg-white"
            />
          </div>
        </div>
      </Card>

      {/* Top Automatic Count Cards for Selected Date */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Present Count Card */}
        <Card className="p-5 border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50/50 to-white shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Present Patients</p>
              <h3 className="text-3xl font-extrabold text-slate-800 mt-1">{summary.present ?? 0}</h3>
              <p className="text-xs text-slate-500 mt-1">
                {selectedDate === today ? "Today's Clinic Check-ins" : `Attended on ${fmtDate(selectedDate)}`}
              </p>
            </div>
            <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
              <UserCheck className="h-6 w-6" />
            </div>
          </div>
        </Card>

        {/* Absent Count Card */}
        <Card
          className="p-5 border-l-4 border-l-rose-500 bg-gradient-to-br from-rose-50/50 to-white shadow-sm cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate("/absent")}
          title="View Absent Patients Follow-ups"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-rose-700">Absent Patients</p>
              <h3 className="text-3xl font-extrabold text-slate-800 mt-1">{totalAbsent}</h3>
              <p className="text-xs text-rose-600 font-medium mt-1 flex items-center gap-1">
                Click to view absent follow-ups <ArrowRight className="h-3 w-3" />
              </p>
            </div>
            <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center text-rose-600">
              <UserX className="h-6 w-6" />
            </div>
          </div>
        </Card>

        {/* Total Scheduled / Expected Card */}
        <Card
          className="p-5 border-l-4 border-l-blue-500 bg-gradient-to-br from-blue-50/50 to-white shadow-sm cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate("/clinic")}
          title="Go to Today's Clinic Sheet"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-blue-700">Total Expected</p>
              <h3 className="text-3xl font-extrabold text-slate-800 mt-1">{summary.expected ?? 0}</h3>
              <p className="text-xs text-slate-500 mt-1">
                {(summary.pending ?? 0) > 0 ? `${summary.pending} pending unmarked` : "All attendance marked"}
              </p>
            </div>
            <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
              <CalendarCheck className="h-6 w-6" />
            </div>
          </div>
        </Card>
      </div>

      {/* Present Patients List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display font-bold text-slate-800 text-base flex items-center gap-2">
            <span>List of Present Patients</span>
            <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5">
              {presentPatients.length}
            </Badge>
          </h4>
          {search && (
            <p className="text-xs text-slate-500">
              Filtered from {summary.present ?? 0} present patients
            </p>
          )}
        </div>

        {loading ? (
          <Loading label={`Loading present patients for ${fmtDate(selectedDate)}...`} />
        ) : presentPatients.length === 0 ? (
          <EmptyState
            icon={UserCheck}
            title={search ? "No matching patients found" : `No present patients on ${fmtDate(selectedDate)}`}
            subtitle={
              search
                ? "Try searching with a different name, phone number, or patient code."
                : selectedDate === today
                ? "Patients marked 'Present' in Today's Clinic will appear here."
                : "No patients were recorded as present on this date."
            }
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {presentPatients.map((att, idx) => (
              <Card
                key={att.id || att.patient_id || idx}
                className="p-4 hover:border-emerald-300 hover:shadow-md transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start gap-3">
                    <Avatar
                      className="h-11 w-11 cursor-pointer ring-2 ring-emerald-500/20"
                      onClick={() => navigate(`/patients/${att.patient_id}`)}
                    >
                      <AvatarFallback className="bg-emerald-50 text-emerald-700 font-bold text-sm">
                        {initials(att.patient_name)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <p
                          className="font-bold text-slate-900 truncate hover:text-emerald-700 cursor-pointer text-sm"
                          onClick={() => navigate(`/patients/${att.patient_id}`)}
                        >
                          {att.patient_name}
                        </p>
                        <Badge className="bg-emerald-600 hover:bg-emerald-700 text-[10px] font-semibold px-2 py-0.5 uppercase tracking-wider">
                          Present
                        </Badge>
                      </div>

                      <p className="text-xs font-mono font-medium text-slate-500 mt-0.5">
                        {att.patient_code || "BK-PATIENT"}
                      </p>
                    </div>
                  </div>

                  {/* Problem & Treatment info */}
                  {att.problem && (
                    <div className="mt-3 flex items-start gap-1.5 text-xs text-slate-700 bg-slate-50 p-2 rounded-md">
                      <Stethoscope className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                      <span className="line-clamp-2">{att.problem}</span>
                    </div>
                  )}

                  {/* Badges & Meta */}
                  <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                    {att.payment_type && (
                      <Badge variant="outline" className="text-[11px] capitalize bg-white text-slate-700">
                        {att.payment_type.replace(/_/g, " ")}
                      </Badge>
                    )}

                    {att.session_fee != null && (
                      <Badge variant="outline" className="text-[11px] bg-white text-slate-700">
                        Fee: {inr(att.session_fee)}
                      </Badge>
                    )}

                    {att.advance_balance != null && att.payment_type === "advance" && (
                      <Badge
                        variant="outline"
                        className={`text-[11px] ${
                          att.advance_balance <= 0
                            ? "bg-rose-50 text-rose-700 border-rose-200"
                            : "bg-emerald-50 text-emerald-700 border-emerald-200"
                        }`}
                      >
                        Adv Bal: {inr(att.advance_balance)}
                      </Badge>
                    )}

                    {att.marked_at && (
                      <Badge variant="secondary" className="text-[10px] text-slate-500 font-normal ml-auto">
                        <Clock className="h-3 w-3 mr-1" />
                        {att.marked_at.includes("T")
                          ? att.marked_at.split("T")[1].slice(0, 5)
                          : att.marked_at.slice(11, 16)}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Bottom Actions */}
                <div className="flex flex-wrap items-center gap-1.5 mt-4 pt-3 border-t border-slate-100">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs font-semibold text-bk-blue hover:bg-blue-50 border-blue-200 gap-1"
                    onClick={() => {
                      setSelectedForTreatment(att);
                      setTreatmentOpen(true);
                    }}
                  >
                    <Stethoscope className="h-3.5 w-3.5" /> Treatment
                  </Button>

                  {att.phone && (
                    <>
                      <a href={telLink(att.phone)}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-2 text-xs text-bk-blue hover:bg-blue-50 border-blue-200"
                        >
                          <Phone className="h-3.5 w-3.5" />
                        </Button>
                      </a>

                      <a
                        href={waLink(
                          att.whatsapp || att.phone,
                          `Namaste ${att.patient_name} ji, BK Spine Care Physiotherapy Clinic me aapka swagat hai.`
                        )}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-2 text-xs text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                        </Button>
                      </a>
                    </>
                  )}

                  <Button
                    variant="default"
                    size="sm"
                    className="h-8 text-xs bg-slate-800 hover:bg-slate-900 px-3 ml-auto"
                    onClick={() => navigate(`/patients/${att.patient_id}`)}
                  >
                    Profile
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <TreatmentRecordDialog
        open={treatmentOpen}
        onOpenChange={setTreatmentOpen}
        attendance={selectedForTreatment}
        onSaved={() => loadData(selectedDate)}
      />
    </div>
  );
}
